import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, isBefore, differenceInDays } from 'date-fns';
import { Monitor, Pencil, Ban, RefreshCw, Plus, Search, ChevronDown } from 'lucide-react';
import { licenseService } from '../api/services/licenseService';
import { customerService } from '../api/services/customerService';
import LicenseModal from '../components/licenses/LicenseModal';
import ActivationsModal from '../components/licenses/ActivationsModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import License, { Customer } from '../types/domain';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGate } from '../components/common/PermissionGate';
import ActionMenu, { ActionMenuSection } from '../components/common/ActionMenu';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
} from '@license-management/ui';

const LicenseManagement: React.FC = () => {
  const permissions = usePermissions();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedTab, setSelectedTab] = React.useState<'active' | 'revoked' | 'expired'>('active');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedLicense, setSelectedLicense] = React.useState<License | undefined>(undefined);
  const [modalMode, setModalMode] = React.useState<'create' | 'edit' | 'view'>('create');
  
  // State for activations modal
  const [isActivationsModalOpen, setIsActivationsModalOpen] = React.useState(false);
  const [selectedLicenseForActivations, setSelectedLicenseForActivations] = React.useState<License | undefined>(undefined);
  
  const [confirmDialog, setConfirmDialog] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const [expandedCustomers, setExpandedCustomers] = React.useState<Set<string>>(new Set());

  // Add state for permanent delete confirmation
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    customer?: Customer;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    customer: undefined,
    onConfirm: () => {},
  });

  const queryClient = useQueryClient();

  const { data: licenses = [], isLoading: isLoadingLicenses, error: licenseError } = useQuery({
    queryKey: ['licenses'],
    queryFn: async () => {
      console.log('[LicenseManagement] Fetching licenses');
      const result = await licenseService.getAllLicenses();
      console.log('[LicenseManagement] Fetched licenses:', result);
      return result;
    }
  });

  const { data: customers = [], isLoading: isLoadingCustomers, error: customerError } = useQuery<Customer[]>({
    queryKey: ['customers', { includeDeleted: true }],
    queryFn: () => customerService.getCustomers({ includeDeleted: true })
  });

  const revokeLicenseMutation = useMutation({
    mutationFn: (licenseId: string) => licenseService.revokeLicense(licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    }
  });

  const reactivateLicenseMutation = useMutation({
    mutationFn: (licenseId: string) => licenseService.reactivateLicense(licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    }
  });

  const createLicenseMutation = useMutation({
    mutationFn: (data: any) => licenseService.createLicense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setIsModalOpen(false);
    }
  });

  const updateLicenseMutation = useMutation({
    mutationFn: ({ licenseId, data }: { licenseId: string; data: any }) => 
      licenseService.updateLicense(licenseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setIsModalOpen(false);
    }
  });

  // Add mutation for checking if a customer can be permanently deleted
  const checkCustomerDeletableMutation = useMutation({
    mutationFn: (customerId: string) => customerService.checkCustomerDeletable(customerId),
  });

  // Add mutation for permanently deleting a customer
  const permanentDeleteCustomerMutation = useMutation({
    mutationFn: (customerId: string) => customerService.hardDeleteCustomer(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    },
  });

  const handleCreateLicense = () => {
    setSelectedLicense(undefined);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditLicense = (license: License) => {
    setSelectedLicense(license);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLicense(undefined);
  };
  
  // Handler for opening activations modal
  const handleOpenActivations = (license: License) => {
    setSelectedLicenseForActivations(license);
    setIsActivationsModalOpen(true);
  };

  // Handler for closing activations modal
  const handleCloseActivations = () => {
    setIsActivationsModalOpen(false);
    setSelectedLicenseForActivations(undefined);
  };

  const handleModalSubmit = async (data: any) => {
    if (modalMode === 'create') {
      await createLicenseMutation.mutateAsync(data);
    } else if (modalMode === 'edit' && selectedLicense) {
      await updateLicenseMutation.mutateAsync({
        licenseId: selectedLicense.licenseId,
        data
      });
    }
  };

  const handleRevokeLicense = (license: License) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke License',
      message: `Are you sure you want to revoke the license ${license.licenseKey}?`,
      onConfirm: async () => {
        await revokeLicenseMutation.mutateAsync(license.licenseId);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleReactivateLicense = (license: License) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reactivate License',
      message: `Are you sure you want to reactivate the license ${license.licenseKey}?`,
      onConfirm: async () => {
        await reactivateLicenseMutation.mutateAsync(license.licenseId);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Handler for permanently deleting a customer
  const handlePermanentDeleteCustomer = async (customer: Customer) => {
    try {
      // First check if customer can be deleted
      const result = await checkCustomerDeletableMutation.mutateAsync(customer.customerId);
      
      if (result.canDelete) {
        // Customer can be deleted, show confirmation
        setPermanentDeleteConfirm({
          isOpen: true,
          title: 'Permanently Delete Customer',
          message: `Are you sure you want to permanently delete ${customer.name} and all associated licenses and activations? This action cannot be undone.`,
          customer,
          onConfirm: async () => {
            // Execute permanent deletion
            await permanentDeleteCustomerMutation.mutateAsync(customer.customerId);
            setPermanentDeleteConfirm(prev => ({ ...prev, isOpen: false }));
          }
        });
      } else {
        // Show why customer can't be deleted
        setPermanentDeleteConfirm({
          isOpen: true,
          title: 'Cannot Delete Customer',
          message: result.message || 'This customer cannot be permanently deleted at this time.',
          onConfirm: () => {
            setPermanentDeleteConfirm(prev => ({ ...prev, isOpen: false }));
          }
        });
      }
    } catch (error) {
      console.error('Error checking if customer can be deleted:', error);
    }
  };

  const toggleCustomer = (customerId: string) => {
    setExpandedCustomers(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) {
        next.delete(customerId);
      } else {
        next.add(customerId);
      }
      return next;
    });
  };

  const filteredLicenses = React.useMemo(() => {
    // Re-calculate 'now' inside useMemo to ensure it's fresh on dependency change
    const currentNow = new Date(); 
    return licenses
      .filter(license => {
        const customer = customers.find(c => c.customerId === license.customerId);
        const matchesSearch = 
          license.licenseKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          format(parseISO(license.validUntil), 'MMMM do, yyyy').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (customer?.name.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
          (license.modules || []).some((moduleId: string) => 
            moduleId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            moduleId.replace(/_/g, ' ').toLowerCase().includes(searchTerm.toLowerCase())
          );

        const isExpired = isBefore(parseISO(license.validUntil), currentNow); 

        if (selectedTab === 'active') {
          return matchesSearch && license.status === 'active' && !isExpired; 
        } else if (selectedTab === 'revoked') {
          return matchesSearch && license.status === 'revoked';
        } else { // 'expired' tab
          return matchesSearch && license.status !== 'revoked' && isExpired; 
        }
      })
      .map(license => {
        const customer = customers.find(c => c.customerId === license.customerId);
        return {
          ...license,
          customerDeleted: !!customer?.deletedAt,
        };
      });
  // Remove 'now' from dependencies, rely on other dependencies triggering re-calculation
  }, [licenses, customers, searchTerm, selectedTab]); 

  // Group licenses by customer
  const groupedLicenses = React.useMemo(() => {
    const groups = new Map<string, { customer: Customer; licenses: License[] }>();
    const deletedCustomerLicenses: License[] = [];
    
    filteredLicenses.forEach(license => {
      const customer = customers.find(c => c.customerId === license.customerId);
      
      // If customer is null or undefined, add to deletedCustomerLicenses array
      if (!customer) {
        deletedCustomerLicenses.push(license);
        return;
      }

      if (!groups.has(customer.customerId)) {
        groups.set(customer.customerId, {
          customer,
          licenses: []
        });
      }
      groups.get(customer.customerId)?.licenses.push(license);
    });
    
    // If we have any licenses without associated customers, create a "Deleted Customers" group
    if (deletedCustomerLicenses.length > 0) {
      // Create a placeholder customer for deleted customers
      const deletedCustomerGroup: Customer = {
        customerId: 'deleted-customers',
        name: 'Unknown Customers (Deleted)',
        email: '',
        country: '',
        type: 'organization',
        status: 'inactive',
        inheritLicenses: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: new Date().toISOString(),
      };
      
      groups.set('deleted-customers', {
        customer: deletedCustomerGroup,
        licenses: deletedCustomerLicenses
      });
    }

    return Array.from(groups.values());
  }, [filteredLicenses, customers]);

  // Calculate tab counts - MUST be before conditional returns to comply with React hooks rules
  const tabCounts = React.useMemo(() => {
    const currentNow = new Date();
    return {
      active: licenses.filter(l => l.status === 'active' && !isBefore(parseISO(l.validUntil), currentNow)).length,
      revoked: licenses.filter(l => l.status === 'revoked').length,
      expired: licenses.filter(l => l.status !== 'revoked' && isBefore(parseISO(l.validUntil), currentNow)).length,
    };
  }, [licenses]);

  const formatModuleName = (moduleId: string): string => {
    // Remove mod_ prefix and format the rest
    return moduleId
      .replace(/^mod_/i, '')  // Case-insensitive removal of mod_ prefix
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoadingLicenses || isLoadingCustomers) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const error = licenseError || customerError;
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage
          message={error instanceof Error ? error.message : String(error)}
          onRetry={() => queryClient.invalidateQueries({ queryKey: ['licenses', 'customers'] })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Management"
        description="Manage software licenses across all customers"
        actions={
          <PermissionGate resource="licenses" action="create">
            <Button onClick={handleCreateLicense} leftIcon={<Plus className="h-4 w-4" />}>
              Create License
            </Button>
          </PermissionGate>
        }
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search licenses, customers, modules..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      <div className="flex gap-2">
        {(['active', 'revoked', 'expired'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedTab === tab
                ? 'bg-primary-100 text-primary-700'
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)} ({tabCounts[tab]})
          </button>
        ))}
      </div>

      <div className="space-y-8">
        {groupedLicenses.map(({ customer, licenses }) => (
          <Card key={customer.customerId} className="overflow-hidden">
            <div
              className={`px-6 py-4 border-b border-zinc-200 flex justify-between items-center ${customer.deletedAt ? 'bg-zinc-100' : ''}`}
            >
              <div className="flex items-center cursor-pointer hover:bg-zinc-50 rounded-lg px-2 py-1 -mx-2" onClick={() => toggleCustomer(customer.customerId)}>
                <h3 className="text-lg font-semibold text-zinc-900">
                  {customer.name}
                  {customer.deletedAt && <span className="ml-2 text-zinc-500 font-normal">(Deleted)</span>}
                </h3>
                <div className="flex items-center ml-4">
                  <span className="text-sm text-zinc-500 mr-2">{licenses.length} licenses</span>
                  <ChevronDown
                    className={`w-5 h-5 text-zinc-400 transform transition-transform ${expandedCustomers.has(customer.customerId) ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {customer.deletedAt && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handlePermanentDeleteCustomer(customer)}
                  title="Permanently delete customer and all associated data"
                >
                  Permanently Delete
                </Button>
              )}
            </div>
            {expandedCustomers.has(customer.customerId) && (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-1/4">
                      License Key
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[9%]">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[9%]">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[9%]">
                      Activations
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[18%]">
                      Modules
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[12%]">
                      Valid Until
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider w-[18%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {licenses.map(license => (
                    <tr key={license.licenseId} className={license.customerDeleted ? 'bg-zinc-50' : 'hover:bg-zinc-50 transition-colors'}>
                      <td className={`px-3 py-3 text-sm font-medium text-zinc-900 ${
                        license.status === 'active' &&
                        differenceInDays(parseISO(license.validUntil), new Date()) <= 30
                          ? 'bg-amber-50'
                          : ''
                      }`}>
                        <div className="flex items-center">
                          <span className="font-mono text-sm break-all">{license.licenseKey}</span>
                          {license.status === 'active' &&
                           differenceInDays(parseISO(license.validUntil), new Date()) <= 30 && (
                            <Badge variant="warning" size="sm" className="ml-2">
                              Expires in {differenceInDays(parseISO(license.validUntil), new Date())} days
                            </Badge>
                          )}
                          {license.customerDeleted && (
                            <Badge variant="default" size="sm" className="ml-2">
                              Customer deleted
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-zinc-600 capitalize">
                        {license.type}
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant={license.status === 'active' ? 'success' : 'error'}>
                          {license.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <div className="flex items-center">
                          <span className={`${
                            license.currentActivations >= license.maxActivations
                              ? 'text-red-600 font-medium'
                              : license.currentActivations > license.maxActivations * 0.75
                              ? 'text-amber-600'
                              : 'text-zinc-600'
                          }`}>
                            {license.currentActivations} / {license.maxActivations}
                          </span>
                          {license.currentActivations >= license.maxActivations && (
                            <Badge variant="error" size="sm" className="ml-2">
                              Full
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1 items-center">
                          {(Array.isArray(license.modules) ? license.modules : [])
                            .filter((m): m is string => typeof m === 'string')
                            .map((moduleId) => {
                              const displayName = formatModuleName(moduleId);
                              const baseModuleId = moduleId.replace(/^mod_/i, '').toLowerCase();

                              return (
                                <Badge
                                  key={moduleId}
                                  variant={
                                    baseModuleId.includes('oncology')
                                      ? 'info'
                                      : baseModuleId.includes('pathology')
                                      ? 'success'
                                      : 'default'
                                  }
                                  size="sm"
                                >
                                  {displayName}
                                </Badge>
                              );
                            })}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-zinc-600">
                        {format(parseISO(license.validUntil), 'PPP')}
                      </td>
                      <td className="px-3 py-3 text-right text-sm font-medium">
                        {license.customerDeleted ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenActivations(license)}
                              leftIcon={<Monitor className="h-4 w-4" />}
                            >
                              Activations
                            </Button>
                            <span className="text-zinc-400 italic text-xs">Soft-deleted</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenActivations(license)}
                              leftIcon={<Monitor className="h-4 w-4" />}
                            >
                              Activations
                            </Button>
                            {license.status !== 'revoked' && permissions.canUpdateLicenses() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditLicense(license)}
                                leftIcon={<Pencil className="h-4 w-4" />}
                              >
                                Edit
                              </Button>
                            )}
                            {(license.status === 'active' || license.status === 'revoked') && permissions.canUpdateLicenses() && (
                              <ActionMenu
                                sections={[
                                  {
                                    title: 'Status',
                                    items: license.status === 'active'
                                      ? [
                                          {
                                            label: 'Revoke',
                                            icon: <Ban className="h-4 w-4" />,
                                            onClick: () => handleRevokeLicense(license),
                                            variant: 'danger',
                                          },
                                        ]
                                      : [
                                          {
                                            label: 'Reactivate',
                                            icon: <RefreshCw className="h-4 w-4" />,
                                            onClick: () => handleReactivateLicense(license),
                                          },
                                        ],
                                  },
                                ] as ActionMenuSection[]}
                              />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customer.deletedAt && (
                <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end">
                  <Button
                    variant="danger"
                    onClick={() => handlePermanentDeleteCustomer(customer)}
                  >
                    Permanently Delete
                  </Button>
                </div>
              )}
            </div>
            )}
          </Card>
        ))}
      </div>

      <LicenseModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        license={selectedLicense}
        customers={customers}
        mode={modalMode}
        isSubmitting={createLicenseMutation.isPending || updateLicenseMutation.isPending}
      />
      
      {/* Add Activations Modal */}
      <ActivationsModal
        isOpen={isActivationsModalOpen}
        onClose={handleCloseActivations}
        licenseKey={selectedLicenseForActivations?.licenseKey}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <ConfirmDialog
        isOpen={permanentDeleteConfirm.isOpen}
        title={permanentDeleteConfirm.title}
        message={permanentDeleteConfirm.message}
        onConfirm={permanentDeleteConfirm.onConfirm}
        onCancel={() => setPermanentDeleteConfirm(prev => ({ ...prev, isOpen: false }))}
        severity="high"
      />
    </div>
  );
};

export default LicenseManagement;
